import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Alert, Button, Form, Input, Select, Space, Table, Tag, Typography, Upload, message } from 'antd';
import type { UploadProps } from 'antd';
import { useUser } from '../../contexts/UserContext';
import { canAccessResourcesAdmin } from '../../components/AppShell/appNavAccess';
import { RESOURCE_CATALOG } from '../../content/resources/catalog';
import type { ResourceItem, ResourceKind, ResourceVisibility } from '../../content/resources/types';
import {
  deletePersistedResource,
  getResourceCatalogPersistContract,
  listPersistedResources,
  savePersistedResource,
} from '../../content/resources/resourceCatalogClient';
import {
  slugifyResourceTitle,
} from '../../content/resources/resourceCatalogStore';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

interface ResourceFormValues {
  title: string;
  slug: string;
  summary: string;
  kind: ResourceKind;
  visibility: ResourceVisibility;
  bodyMarkdown?: string;
  tags?: string;
}

export const ResourcesAdminPage: React.FC = () => {
  const { user, isAuthenticated } = useUser();
  const [form] = Form.useForm<ResourceFormValues>();
  const [overlay, setOverlay] = useState<ResourceItem[]>([]);
  const [htmlBody, setHtmlBody] = useState<string | undefined>();
  const [editingId, setEditingId] = useState<string | null>(null);

  const refresh = () => {
    void listPersistedResources().then(setOverlay);
  };

  useEffect(() => {
    refresh();
  }, []);

  if (!isAuthenticated) {
    return <Navigate to="/login?next=/admin/resources" replace />;
  }
  if (!canAccessResourcesAdmin(user)) {
    // Do not bounce to /resources — that looked like a routing bug.
    return (
      <div style={{ padding: 24, maxWidth: 640 }} data-testid="resources-admin-forbidden">
        <Title level={3}>Resources admin</Title>
        <Alert
          type="warning"
          showIcon
          message="Platform admin role required"
          description={
            <span>
              Signed in as <Text code>{user?.email ?? user?.username}</Text> with role{' '}
              <Text code>{user?.role ?? 'unknown'}</Text>. This page needs{' '}
              <Text code>admin</Text> / <Text code>superuser</Text> (or manage:platform). Public
              library stays at <Link to="/resources">/resources</Link>. Subscription-based feature
              codes will refine this gate in the subscriptions WBS.
            </span>
          }
        />
      </div>
    );
  }

  const onFinish = (values: ResourceFormValues) => {
    const slug = (values.slug || slugifyResourceTitle(values.title)).trim();
    if (!slug) {
      message.error('Slug is required');
      return;
    }
    const item: ResourceItem = {
      id: editingId ?? `res-admin-${slug}`,
      slug,
      title: values.title.trim(),
      summary: values.summary.trim(),
      kind: values.kind,
      visibility: values.visibility,
      publishedAt: new Date().toISOString().slice(0, 10),
      tags: values.tags
        ? values.tags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
        : undefined,
      bodyMarkdown: values.bodyMarkdown?.trim() || undefined,
      htmlBody: htmlBody?.trim() || undefined,
    };
    if (!item.bodyMarkdown && !item.htmlBody && !item.htmlPath) {
      message.warning('Add markdown body or upload HTML so the article has content.');
    }
    void savePersistedResource(item).then(() => {
      message.success(
        editingId
          ? `Resource updated (${getResourceCatalogPersistContract().mode})`
          : `Resource published (${getResourceCatalogPersistContract().mode})`
      );
      setEditingId(null);
      setHtmlBody(undefined);
      form.resetFields();
      refresh();
    });
  };

  const uploadProps: UploadProps = {
    accept: '.html,text/html',
    maxCount: 1,
    beforeUpload: (file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const text = typeof reader.result === 'string' ? reader.result : '';
        setHtmlBody(text);
        message.success(`Loaded HTML (${file.name})`);
      };
      reader.readAsText(file);
      return false;
    },
    onRemove: () => {
      setHtmlBody(undefined);
    },
  };

  const seedRows = RESOURCE_CATALOG.map((row) => ({ ...row, source: 'seed' as const }));
  const overlayRows = overlay.map((row) => ({ ...row, source: 'overlay' as const }));

  return (
    <div style={{ padding: 24, maxWidth: 1100 }} data-testid="resources-admin">
      <Title level={2}>Resources admin</Title>
      <Paragraph type="secondary">
        Author articles/blogs for the Resources library. Persist mode:{' '}
        <Text code>{getResourceCatalogPersistContract().mode}</Text> (browser-local overlay until a
        durable content API replaces <Text code>resourceCatalogClient</Text>). Other browsers/devices
        will not see these rows yet.
      </Paragraph>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ kind: 'article', visibility: 'public' }}
        style={{ marginBottom: 32 }}
      >
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input
            onBlur={(e) => {
              if (!form.getFieldValue('slug')) {
                form.setFieldValue('slug', slugifyResourceTitle(e.target.value));
              }
            }}
          />
        </Form.Item>
        <Form.Item name="slug" label="Slug" rules={[{ required: true }]}>
          <Input placeholder="my-article-slug" />
        </Form.Item>
        <Form.Item name="summary" label="Summary" rules={[{ required: true }]}>
          <TextArea rows={2} />
        </Form.Item>
        <Space wrap size="large">
          <Form.Item name="kind" label="Kind" rules={[{ required: true }]}>
            <Select
              style={{ width: 160 }}
              options={[
                { value: 'article', label: 'Article' },
                { value: 'blog', label: 'Blog' },
                { value: 'explainer', label: 'Explainer' },
              ]}
            />
          </Form.Item>
          <Form.Item name="visibility" label="Visibility" rules={[{ required: true }]}>
            <Select
              style={{ width: 180 }}
              options={[
                { value: 'public', label: 'Public (guest)' },
                { value: 'authenticated', label: 'Authenticated' },
              ]}
            />
          </Form.Item>
        </Space>
        <Form.Item name="tags" label="Tags (comma-separated)">
          <Input placeholder="methodology, demand" />
        </Form.Item>
        <Form.Item name="bodyMarkdown" label="Markdown body">
          <TextArea rows={6} placeholder="Optional markdown content" />
        </Form.Item>
        <Form.Item label="HTML upload (optional)">
          <Upload {...uploadProps}>
            <Button>Select .html file</Button>
          </Upload>
          {htmlBody ? <Text type="secondary"> HTML loaded ({htmlBody.length} chars)</Text> : null}
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit">
            {editingId ? 'Update resource' : 'Publish resource'}
          </Button>
          {editingId ? (
            <Button
              onClick={() => {
                setEditingId(null);
                setHtmlBody(undefined);
                form.resetFields();
              }}
            >
              Cancel edit
            </Button>
          ) : null}
        </Space>
      </Form>

      <Title level={4}>Authored overlay</Title>
      <Table
        rowKey="id"
        dataSource={overlayRows}
        pagination={false}
        locale={{ emptyText: 'No admin-authored resources yet' }}
        columns={[
          { title: 'Title', dataIndex: 'title' },
          { title: 'Slug', dataIndex: 'slug' },
          {
            title: 'Visibility',
            dataIndex: 'visibility',
            render: (v: string) => <Tag>{v}</Tag>,
          },
          {
            title: 'Actions',
            key: 'actions',
            render: (_, row) => (
              <Space>
                <Button
                  size="small"
                  onClick={() => {
                    setEditingId(row.id);
                    setHtmlBody(row.htmlBody);
                    form.setFieldsValue({
                      title: row.title,
                      slug: row.slug,
                      summary: row.summary,
                      kind: row.kind,
                      visibility: row.visibility,
                      bodyMarkdown: row.bodyMarkdown,
                      tags: row.tags?.join(', '),
                    });
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  danger
                  onClick={() => {
                    void deletePersistedResource(row.id).then(() => {
                      message.success('Removed from overlay');
                      refresh();
                    });
                  }}
                >
                  Remove
                </Button>
                <a href={`/resources/${row.slug}`}>View</a>
              </Space>
            ),
          },
        ]}
      />

      <Title level={4} style={{ marginTop: 32 }}>
        Seed catalog (read-only)
      </Title>
      <Table
        rowKey="id"
        dataSource={seedRows}
        pagination={false}
        columns={[
          { title: 'Title', dataIndex: 'title' },
          { title: 'Slug', dataIndex: 'slug' },
          {
            title: 'Visibility',
            dataIndex: 'visibility',
            render: (v: string) => <Tag>{v}</Tag>,
          },
        ]}
      />
    </div>
  );
};
